/* eslint-disable react/no-array-index-key */
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useTheme } from 'styled-components';
import { SubscriptionCard } from '~/modules/account/components/SubscriptionCard';

import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { UserSubscriptionTypesEnum } from '~/common/enums/user-subscription-types.enum';
import { Modal } from '~/common/components/Modal';
import { Button } from '~/common/components/Button';
import { SwitchToggle } from '~/common/components/SwitchToggle';
import { api } from '~/common/api';

import { BillingFrequencyToggleWrapper, PlansGridWrapper } from './styles';

type BillingFrequency = 'MONTHLY' | 'YEARLY';

interface IPlanApiPricing {
  monthly: string | null;
  yearly: string | null;
  yearlyMonthlyEquivalent: string | null;
  currency: string | null;
}

interface IPlanApiResponse {
  id: string;
  tier: UserSubscriptionTypesEnum;
  pricing: IPlanApiPricing;
  availableBillingFrequencies: BillingFrequency[];
}

interface ISubscriptionPlan {
  id: UserSubscriptionTypesEnum;
  externalId: string | null;
  title: string;
  value: string;
  recurrence: string;
  details: string;
  billingFrequency: BillingFrequency;
  comparisonValue?: string;
  comparisonCaption?: string;
  annualNotice?: string;
  hideAction?: boolean;
  unavailable?: boolean;
}

const PLAN_COPY: Record<
  UserSubscriptionTypesEnum,
  { title: string; details: string; defaultMonthlyPrice: string }
> = {
  [UserSubscriptionTypesEnum.FREE]: {
    title: 'Plano Gratuito',
    defaultMonthlyPrice: '0.00',
    details:
      'Permite acesso aos prompts gratuitos, possibilitando conhecer a plataforma e suas funcionalidades básicas.',
  },
  [UserSubscriptionTypesEnum.BASIC]: {
    title: 'Plano Platinum',
    defaultMonthlyPrice: '29.90',
    details:
      'Acesso a prompts avançados de geração de imagem, com técnicas refinadas de composição, iluminação e estilo para resultados de alta qualidade.',
  },
  [UserSubscriptionTypesEnum.PREMIUM]: {
    title: 'Plano Premium',
    defaultMonthlyPrice: '19.90',
    details:
      'Acesso total aos prompts de imagem mais poderosos da plataforma, com técnicas profissionais de direção de arte, controle criativo detalhado e os melhores resultados possíveis em geração visual.',
  },
};

const YEARLY_PLAN_NOTICE =
  'Este plano tem duração de 12 meses. Para facilitar, o valor pode ser dividido em até 12x no cartão, mas é importante entender que isso não caracteriza um plano mensal, trata-se apenas do parcelamento do valor total anual. Ao contratar, você garante acesso por todo o período de 12 meses. Ao final desse prazo, o plano não é renovado automaticamente.';

interface ISubscribePlansModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SubscribePlansModal: FC<ISubscribePlansModalProps> = ({
  isOpen,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 750px)');
  const { user } = useAuth();
  const { showToast } = useToast();

  const [plans, setPlans] = useState<IPlanApiResponse[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedFrequency, setSelectedFrequency] =
    useState<BillingFrequency>('YEARLY');

  const formatCurrency = useCallback(
    (value: string | null | undefined): string => {
      const numericValue = Number(value || 0);

      return numericValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    []
  );

  const formatInstallmentValue = useCallback(
    (value: string | null | undefined): string => {
      const numericValue = Number(value || 0);

      return `12x ${formatCurrency((numericValue / 12).toFixed(2))}`;
    },
    [formatCurrency]
  );

  useEffect(() => {
    if (!isOpen) return;

    const loadPlans = async (): Promise<void> => {
      try {
        setPlansLoading(true);
        const { data } = await api.get<{ plans: IPlanApiResponse[] }>('/plans');
        setPlans(data.plans || []);
      } catch {
        setPlans([]);
        showToast({
          severity: 'error',
          description:
            'Nao foi possivel carregar os precos atualizados dos planos. Alguns planos podem ficar indisponiveis no momento.',
        });
      } finally {
        setPlansLoading(false);
      }
    };

    loadPlans();
  }, [isOpen, showToast]);

  const plansByTier = useMemo(() => {
    return new Map(plans.map((plan) => [plan.tier, plan]));
  }, [plans]);

  const subscriptions = useMemo<ISubscriptionPlan[]>(() => {
    const freePlan: ISubscriptionPlan = {
      id: UserSubscriptionTypesEnum.FREE,
      externalId: null,
      title: PLAN_COPY[UserSubscriptionTypesEnum.FREE].title,
      value: 'Gratis',
      recurrence: '',
      details: PLAN_COPY[UserSubscriptionTypesEnum.FREE].details,
      billingFrequency: 'MONTHLY',
      hideAction: true,
    };

    const buildPaidPlan = (
      tier: UserSubscriptionTypesEnum.PREMIUM
    ): ISubscriptionPlan => {
      const plan = plansByTier.get(tier);
      const copy = PLAN_COPY[tier];
      const monthlyPriceAvailable = Boolean(
        plan?.id &&
          plan?.availableBillingFrequencies?.includes('MONTHLY') &&
          plan?.pricing?.monthly
      );
      const yearlyPriceAvailable = Boolean(
        plan?.id &&
          plan?.availableBillingFrequencies?.includes('YEARLY') &&
          plan?.pricing?.yearly
      );

      if (selectedFrequency === 'MONTHLY') {
        if (monthlyPriceAvailable && plan?.pricing?.monthly) {
          return {
            id: tier,
            externalId: plan.id,
            title: copy.title,
            value: formatCurrency(plan.pricing.monthly),
            recurrence: '/mês',
            details: copy.details,
            billingFrequency: 'MONTHLY',
          };
        }

        return {
          id: tier,
          externalId: null,
          title: copy.title,
          value: '-',
          recurrence: '',
          details: copy.details,
          billingFrequency: 'MONTHLY',
          unavailable: true,
        };
      }

      if (yearlyPriceAvailable && plan?.pricing?.yearly) {
        return {
          id: tier,
          externalId: plan.id,
          title: copy.title,
          value: formatInstallmentValue(plan.pricing.yearly),
          recurrence: '',
          comparisonValue: plan.pricing.monthly
            ? formatCurrency(plan.pricing.monthly)
            : undefined,
          comparisonCaption: plan.pricing.monthly ? '/mês no mensal' : undefined,
          details: copy.details,
          billingFrequency: 'YEARLY',
          annualNotice: YEARLY_PLAN_NOTICE,
        };
      }

      return {
        id: tier,
        externalId: null,
        title: copy.title,
        value: '-',
        recurrence: '',
        details: copy.details,
        billingFrequency: 'YEARLY',
        annualNotice: YEARLY_PLAN_NOTICE,
        unavailable: true,
      };
    };

    return [
      freePlan,
      buildPaidPlan(UserSubscriptionTypesEnum.PREMIUM),
    ];
  }, [
    formatCurrency,
    formatInstallmentValue,
    plansByTier,
    selectedFrequency,
  ]);

  const handleSelectSubscription = useCallback(
    async (selected: ISubscriptionPlan) => {
      if (!selected) return;

      if (selected.unavailable) {
        showToast({
          severity: 'info',
          description: 'Este plano esta temporariamente indisponivel.',
        });
        return;
      }

      if (selected.id === UserSubscriptionTypesEnum.FREE) {
        return;
      }

      try {
        const {
          data: { url: checkoutUrl },
        } = await api.post('/billing/checkout', {
          planId: selected.externalId,
          billingFrequency: selected.billingFrequency,
          successUrl: `${window.location.origin}/prompts?new-subscription=true`,
          cancelUrl: `${window.location.origin}/prompts?canceled-subscription-change=true`,
        });

        window.location.href = checkoutUrl;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          'Ocorreu um erro ao tentar processar a alteracao do plano. Tente novamente.';

        showToast({
          severity: 'error',
          description: errorMessage,
        });
      }
    },
    [showToast]
  );

  const currentSubscription = useMemo(
    () => ({
      subscription: user?.subscription,
      billingFrequency: user?.subscriptionBillingFrequency,
      provider: user?.subscriptionProvider,
    }),
    [
      user?.subscription,
      user?.subscriptionBillingFrequency,
      user?.subscriptionProvider,
    ]
  );

  return (
    <Modal
      id="subscribe-plans-modal"
      isOpen={isOpen}
      onClose={onClose}
      width={isMobile ? '100%' : '90%'}
      height={isMobile ? '100%' : '650px'}
      title="Assine um plano"
      subTitle="Escolha o plano ideal para desbloquear todos os recursos"
      justifyContent="flex-start"
      closeOnOutsideClick
    >
      <BillingFrequencyToggleWrapper>
        <SwitchToggle
          switches={[
            { key: 'YEARLY', title: 'Anual' },
            {
              key: 'MONTHLY',
              title: 'Mensal',
              disabled:
                user?.subscriptionBillingFrequency === 'YEARLY' &&
                user?.subscription !== UserSubscriptionTypesEnum.FREE &&
                user?.subscriptionStatus !== 'canceled' &&
                user?.subscriptionStatus !== 'refunded',
            },
          ]}
          current={selectedFrequency}
          onChange={(value) => setSelectedFrequency(value as BillingFrequency)}
          selectedTextColor="neutral1000"
          fixedItemWidth="120px"
        />
      </BillingFrequencyToggleWrapper>

      <PlansGridWrapper>
        {!plansLoading &&
          subscriptions.map((plan, index) => (
            <SubscriptionCard
              key={String(index)}
              handleChange={handleSelectSubscription}
              card={plan}
              current={currentSubscription}
            />
          ))}
      </PlansGridWrapper>

      {plansLoading && (
        <Button
          text="Carregando planos..."
          width="100%"
          backgroundColor={theme.neutral900}
          borderColor={theme.neutral800}
          textOrIconColor={theme.neutral300}
          disabled
        />
      )}
    </Modal>
  );
};
